import { SALESFORCE_CONSTANTS } from "$lib/utilities/constants";
import { textToId } from "../dom-helpers";
import { getFullUrl } from "../url-helpers";

/**
 * @typedef {"Salesforce" | "Infor"} FormProvider
 */

/**
 * @typedef {"split" | "stacked" | "slim"} FormLayout
 */

/**
 * @typedef {Object} FormFieldProps
 *
 * @property {string} id
 * @property {string} label
 * @property {"text" | "email" | "phone" | "number" | "textarea" | "checkbox" | "radio" | "single-select" | "multi-select" | "date"} type
 * @property {string[]} [options]
 * @property {boolean} [required]
 * @property {string} [helpText]
 * @property {string} [errorMessage]
 *
 */

/**
 * @typedef {Object} FormFieldContentType
 *
 * @property {FormFieldProps} fields
 *
 */

/**
 * @typedef {Object} FormFieldGroupProps
 *
 * @property {string} label
 * @property {FormFieldContentType[]} fields
 *
 */
/**
 * @typedef {Object} FormFieldGroupContentType
 *
 * @property {FormFieldGroupProps} fields
 *
 */

/**
 * @typedef {Object} FormContentType
 *
 * @property {string} id
 * @property {FormProvider} provider
 * @property {FormLayout} layout
 * @property {string} theme
 * @property {string} title
 * @property {string} description
 * @property {(FormFieldContentType | FormFieldGroupContentType)[]} fields
 * @property {string} [submitButtonLabel]
 * @property {string} [confirmationTitle]
 * @property {string} [confirmationSubtitle]
 * @property {string} [confirmationDescription]
 * @property {Object} [confirmationCta]
 * @property {string} [redirectUrl]
 *
 */

/**
 *
 * @param {*} rawFormData
 * @param {*} currentUrl
 * @returns
 */
export function buildDefaultFormFields(rawFormData = {}, currentUrl) {
  const { id: formId, provider, title, submitButtonLabel, redirectUrl } = rawFormData;

  const defaultSalesforceFields = [
    {
      id: "campaignId",
      name: SALESFORCE_CONSTANTS.fieldNames.campaignId,
      type: "hidden",
      value: formId
    },
    {
      id: "contentPreferences",
      name: SALESFORCE_CONSTANTS.fieldNames.contentPreferences,
      type: "hidden",
      value: null
    },
    {
      id: "redirectUrl",
      name: SALESFORCE_CONSTANTS.fieldNames.redirectUrl,
      type: "hidden",
      value: getFullUrl(redirectUrl, currentUrl?.origin)
    },
    {
      id: "subject",
      name: SALESFORCE_CONSTANTS.fieldNames.subject,
      type: "hidden",
      value: title
    },
    {
      id: "Source_URL__c",
      name: SALESFORCE_CONSTANTS.fieldNames.contactSourceUrl,
      type: "hidden",
      value: currentUrl?.href
    },
    {
      id: `gtmEmailSignUp-${formId}`,
      type: "submit",
      label: submitButtonLabel
    }
  ];

  const defaultInforFields = [
    {
      id: textToId("Infor default lead division name"),
      name: "lead.division.name",
      type: "hidden",
      value: "Obama Presidential Center"
    },
    {
      id: textToId("Infor default lead site name"),
      name: "lead.site.name",
      type: "hidden",
      value: "Obama Presidential Center"
    },
    {
      id: textToId("Infor default lead status"),
      name: "lead.leadStatus",
      type: "hidden",
      value: "New Lead"
    },
    {
      id: textToId("Infor default lead type"),
      name: "lead.leadType",
      type: "hidden",
      value: "Internet Inquiry"
    },
    {
      id: `infor-submit-btn-${formId}`,
      type: "submit",
      label: submitButtonLabel
    }
  ];

  let defaultFormFields = [];

  switch (provider) {
    case "Salesforce":
      // add default fields
      defaultFormFields.push(...defaultSalesforceFields);
      break;
    case "Infor":
      // add default fields
      defaultFormFields.push(...defaultInforFields);
      break;
    default:
      console.error(`Sorry, form provider not supported: ${provider}.`);
  }

  return defaultFormFields;
}

/**
 *
 * @param {*} rawFormData
 * @returns
 */
export function buildFormFields(rawFormData = {}) {
  const { id: formId, fields: rawFormFieldsOrFieldGroups = [] } = rawFormData;

  let transformedFormFields = [];
  const rawFormFields = rawFormFieldsOrFieldGroups.filter(
    (f) => f.sys.contentType.sys.id === "formField"
  );

  for (const formField of rawFormFields) {
    const type = formField.fields.type;

    let formFieldProps = {
      ...formField.fields,
      id: `form-view-${formId}-${textToId(formField.fields.label)}`,
      name: formField.fields.id
    };

    switch (type) {
      case "text":
      case "email":
      case "phone":
      case "checkbox":
      case "radio":
      case "number":
      case "date":
      case "time":
      case "textarea":
        transformedFormFields.push(formFieldProps);
        break;
      case "radio-group":
      case "checkbox-group":
      case "single-select":
      case "multi-select":
        formFieldProps.options = formField.fields.options.map((opt) => ({
          name: formField.fields.id, // checkboxes w/ same name are grouped together
          id: `form-view-${formId}-${textToId(formField.fields.label)}-${textToId(opt)}`,
          label: opt,
          value: opt
        }));
        transformedFormFields.push(formFieldProps);
        break;
      default:
        console.error(`Sorry, input type not supported: ${type}.`);
    }
  }

  return transformedFormFields;
}

/**
 * Build the props for the Form Field Group inside a Form
 */
export function buildFormFieldGroups(rawFormData = {}) {
  const { id, fields: rawFormFieldsOrFieldGroups = [] } = rawFormData;
  return rawFormFieldsOrFieldGroups
    .filter((f) => f.sys.contentType.sys.id === "formFieldGroup")
    .map((formGroup) => ({
      label: formGroup.fields.label,
      fields: buildFormFields({
        id,
        fields: formGroup.fields.fields
      })
    }));
}

export function buildCta(confirmationCta) {
  let ctaProps = { text: "View all newsletters", url: "/newsletters" };
  if (confirmationCta) {
    if (confirmationCta.fields.htmlSnippet) {
      ctaProps = { htmlSnippet: confirmationCta.fields.htmlSnippet };
    } else {
      ctaProps = {
        text: confirmationCta.fields.text,
        url: confirmationCta.fields.destination.fields.url
      };
    }
  }

  return ctaProps;
}

/**
 * convert html time input to Infor-compatible format, i.e., hh:mm AM/PM
 *
 * @param {string} time
 * @returns
 */
export function formatTime(time = "") {
  if (!time) {
    return "";
  }

  const [hour, minute, amPm] = time.trim().split(/:|\s/);

  if (!hour || !minute) {
    return "";
  }

  const hourAsInt = parseInt(hour);
  if (hourAsInt < 12) {
    return `${hourAsInt}:${minute} ${amPm || "AM"}`;
  } else if (hourAsInt === 12) {
    return `12:${minute} ${amPm || "PM"}`;
  } else if (hourAsInt > 12) {
    return `${hourAsInt - 12}:${minute} ${amPm || "PM"}`;
  } else {
    return "";
  }
}

/**
 * @param {Object} params
 */
export function sanitizeFormValues({ formFields = [], formFieldGroups = [], rawFormValues = {} }) {
  const allFormFields = formFields.concat(formFieldGroups.map((group) => group.fields).flat());

  let sanitizedFieldValues = {};
  for (const { name, type } of allFormFields) {
    const value = rawFormValues[name];
    if (["termsOfService"].includes(name)) {
      // no-op
    } else if (type === "time") {
      sanitizedFieldValues[name] = formatTime(value);
    } else if (value) {
      sanitizedFieldValues[name] = value;
    }
  }

  return sanitizedFieldValues;
}
